import {
  ContentModule,
  ModuleProperties,
  useShowInstructions,
  useLocale,
} from '@backstage-components/base';
import {useGuestConsent} from '@backstage-components/guest-consent-provider';
import {
  Accordion,
  Button,
  ButtonProps,
  chakra,
  ChakraProps,
  Flex,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalOverlay,
  shouldForwardProp,
  Text,
  useDisclosure,
} from '@chakra-ui/react';
import {css, cx} from '@emotion/css';
import {isValidMotionProp, motion} from 'framer-motion';
import {useSubscription} from 'observable-hooks';
import {useMemo, useState, type FC, useEffect} from 'react';
import * as Styled from './CookieBanner.styled';
import {
  ComponentDefinition,
  cssVarNamespace,
  reactName as namespace,
  SchemaType,
} from './CookieBannerDefinition';
import {
  privacyPreferences,
  contentTranslations,
  isSupportedLocale,
  type CookieCategoryItem,
  type PrivacyPreferences,
  type SupportedLocaleType,
} from './content';
import {CookieCategory} from './subComponents/CookieCategory';

export interface ICookieBannerProps extends ModuleProperties, SchemaType {}

export type CookieBannerComponentDefinition = ContentModule<
  'CookieBanner',
  ICookieBannerProps & SchemaType
>;

const variants = {
  visible: {
    opacity: 1,
    display: 'flex',
    y: 0,
    transition: {duration: 0.75, ease: 'easeOut'},
  },
  hidden: {
    opacity: 0,
    y: 40,
    transition: {duration: 0.45},
    transitionEnd: {display: 'none'},
  },
};

const ChakraBox = chakra(motion.div, {
  /**
   * Allow motion props and non-Chakra props to be forwarded.
   */
  shouldForwardProp: (prop) =>
    isValidMotionProp(prop) || shouldForwardProp(prop),
});

const sharedButtonStyles: ButtonProps = {
  borderRadius: '6px',
  padding: '0.5rem 1rem',
  height: 12,
};

export const CookieBannerComponent: FC<CookieBannerComponentDefinition> = (
  definition
) => {
  const analyticsConsent = useGuestConsent('analytics');
  const isPreview = definition.config.scope === 'admin';

  // Get the user's preferred locale
  const [userLocale, setUserLocale] = useState<SupportedLocaleType>('en');
  const locale = useLocale();

  const storedPrivacyPreferences = useMemo<PrivacyPreferences>(() => {
    // Create a copy so the original is not modified
    const preferences = privacyPreferences['en'].slice();
    for (const pref of preferences) {
      if (pref.key === 'analytics' && analyticsConsent === 'accepted') {
        pref.status.key = 'enabled';
      } else if (pref.key === 'analytics') {
        pref.status.key = 'disabled';
      }
    }
    return preferences;
  }, [analyticsConsent]);

  useEffect(() => {
    const newLocale = locale.split('-')[0] || locale;
    // Transform `en-US` to `en` for content translations
    setUserLocale(isSupportedLocale(newLocale) ? newLocale : 'en');
  }, [locale]);

  // Whether or not the user has responded to the cookie banner
  const hasResponded = useMemo(
    () => analyticsConsent !== 'default' && !isPreview,
    [analyticsConsent, isPreview]
  );

  // Set the initial state of the privacy preferences based on the previously saved preferences, or the default preferences
  const [privacyPreferenceStatus, setPrivacyPreferenceStatus] =
    useState<PrivacyPreferences>(storedPrivacyPreferences);

  const {props, id} = definition;
  const {theme, accentColor, position} = props;

  const policyContent = useMemo(
    () =>
      isSupportedLocale(userLocale) && userLocale !== 'en'
        ? contentTranslations[userLocale]
        : {...contentTranslations['en'], ...props.policyContent},
    [props.policyContent, userLocale]
  );

  // Set colors based on theme with css variable overrides available to content authors
  const textColor = `var(--${cssVarNamespace}TextColor, ${
    theme === 'dark' ? '#fff' : '#000'
  })`;
  const backgroundColor = `var(--${cssVarNamespace}BackgroundColor, ${
    theme === 'dark' ? '#191919' : '#ededed'
  })`;

  const {isOpen, onOpen, onClose} = useDisclosure();

  const styleClassName = css`
    ${definition.style}
    ${definition.props.styleAttr}
  `;

  // Reused styles
  const bannerPositionStyles: ChakraProps = useMemo(
    () => ({
      position: 'fixed',
      bottom: {
        base: 0,
        md: '1.25rem',
      },
      left: {
        base: 0,
        md: position === 'Bottom Left' && !isPreview ? '1.25rem' : 'auto',
      },
      right: {
        base: 0,
        md: position === 'Bottom Right' && !isPreview ? '1.25rem' : 'auto',
      },
      zIndex: 25,
    }),
    [position, isPreview]
  );

  const secondaryButtonStyles: ButtonProps = useMemo(
    () => ({
      color: textColor,
      backgroundColor: 'transparent',
      borderColor: textColor,
      border: '1px solid',
      ...sharedButtonStyles,
      _hover: {
        backgroundColor: textColor,
        color: backgroundColor,
      },
    }),
    [textColor, backgroundColor]
  );

  const primaryButtonStyles: ButtonProps = useMemo(
    () => ({
      color: '#fff',
      backgroundColor: accentColor,
      ...sharedButtonStyles,
      _hover: {
        opacity: 0.6,
      },
    }),
    [accentColor]
  );

  const onAcceptAll = (): void => {
    broadcast({type: `GuestCookieConsent:accept-all`, meta: {}});
  };

  const onRejectAll = (): void => {
    broadcast({type: `GuestCookieConsent:reject-all`, meta: {}});
  };

  const onCustomize = (): void => {
    onOpen();
    broadcast({type: `${namespace}:on-customize`, meta: {}});
  };

  const {observable, broadcast} = useShowInstructions(
    ComponentDefinition.instructions,
    definition
  );

  const onSavePreferences = (): void => {
    const analyticsPref = privacyPreferenceStatus.find(
      (pref) => pref.key === 'analytics'
    );
    // Update the analytics preferences for the user
    broadcast({
      type: 'GuestCookieConsent:save-preferences',
      meta: {
        analytics:
          analyticsPref?.status.key === 'enabled'
            ? 'accepted'
            : analyticsPref?.status.key === 'disabled'
            ? 'rejected'
            : 'default',
      },
    });
    onClose();
  };

  useSubscription(observable, (inst) => {
    if (inst.type === `${namespace}:launch-customize-preferences`) {
      onOpen();
    }
  });

  const onSwitchChange = (index: number, item: CookieCategoryItem): void => {
    const newStatus = item.status.key === 'enabled' ? 'disabled' : 'enabled';
    const newPrivacyPreferenceStatus = [
      ...(privacyPreferenceStatus || privacyPreferences),
    ];
    const category = newPrivacyPreferenceStatus.find((x) => x.key === item.key);
    if (category && typeof category === 'object') {
      category.status.key = newStatus;
      setPrivacyPreferenceStatus(newPrivacyPreferenceStatus);
    }
  };

  return (
    <Styled.Wrapper
      id={id}
      className={cx(styleClassName, definition.mid)}
      style={{
        pointerEvents: isPreview ? 'none' : 'all',
      }}
    >
      <Modal
        isOpen={isOpen}
        onClose={() => {
          onClose();
          broadcast({type: `${namespace}:on-close-preferences`, meta: {}});
        }}
        size="4xl"
        motionPreset="scale"
        isCentered={false}
        closeOnOverlayClick={false}
      >
        <ModalOverlay />
        <ModalContent backgroundColor={backgroundColor} px={5} py={8}>
          <ModalCloseButton color={textColor} size="lg" borderRadius="50%" />
          <ModalBody padding={{base: '0', md: '1.25rem'}}>
            <Text
              color={textColor}
              fontSize={'1.5625rem'}
              mb={3}
              className="cookie-preferences-title"
            >
              {policyContent.preferencesTitle}
            </Text>
            <Text
              color={textColor}
              fontSize={'1rem'}
              className="cookie-banner-text"
            >
              {policyContent.preferencesText}
            </Text>
            <Accordion
              allowMultiple
              mt={6}
              display="flex"
              flexDir="column"
              gap="1rem"
            >
              {privacyPreferenceStatus.map((item, index) => (
                <CookieCategory
                  key={index}
                  theme={theme}
                  item={item}
                  index={index}
                  accentColor={accentColor}
                  textColor={textColor}
                  userLocale={userLocale}
                  onSwitchChange={onSwitchChange}
                />
              ))}
            </Accordion>
          </ModalBody>

          <ModalFooter>
            <Button
              {...primaryButtonStyles}
              w="100%"
              maxW="19.375rem"
              onClick={onSavePreferences}
              className="btn-preferences-save"
            >
              {policyContent.btnSave}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <ChakraBox
        initial="hidden"
        animate={hasResponded || isOpen ? 'hidden' : 'visible'}
        variants={variants}
        backgroundColor={backgroundColor}
        padding="1.5rem 1.75rem"
        maxW={{
          base: 'none',
          md: '36.75rem',
        }}
        display="flex"
        flexDirection="column"
        gap={'0.75rem'}
        className="lcd-cookie-banner"
        data-testid="cookie-banner"
        pos={isPreview ? 'relative' : 'fixed'}
        boxShadow="10px 10px 25px 5px rgba(0,0,0,0.5)"
        {...(!isPreview && {...bannerPositionStyles})}
      >
        <Text
          color={textColor}
          fontSize={'1.25rem'}
          fontWeight={'bold'}
          className="cookie-banner-title"
        >
          {policyContent.bannerTitle}
        </Text>
        <Text
          color={textColor}
          fontSize={'1rem'}
          className="cookie-banner-text"
        >
          {policyContent.bannerText}
        </Text>
        <Flex gap="1rem" mt="0.75rem">
          <Button
            {...secondaryButtonStyles}
            onClick={onCustomize}
            className="btn-ck-customize"
          >
            {policyContent.btnCustomize}
          </Button>
          <Button
            {...secondaryButtonStyles}
            onClick={onRejectAll}
            className="btn-ck-reject"
          >
            {policyContent.btnRejectAll}
          </Button>
          <Button
            {...primaryButtonStyles}
            onClick={onAcceptAll}
            className="btn-ck-accept"
          >
            {policyContent.btnAcceptAll}
          </Button>
        </Flex>
      </ChakraBox>
    </Styled.Wrapper>
  );
};
